import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatError } from "@/lib/api";
import { MapPin, Mail, Phone, Facebook, Youtube } from "lucide-react";

const socials = [
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=100063563125101", label: "Facebook" },
  { icon: Youtube, href: "https://youtube.com/@icdhhaiti9703?si=zhZQ7Oym5ZtBxcbE", label: "YouTube" },
];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, type: "contact" });
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const field = "w-full h-12 bg-transparent border-b border-icdh-white/25 focus:border-icdh-red outline-none text-icdh-white placeholder:text-icdh-white/40 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-icdh-navy text-icdh-white py-28 md:py-40">
      <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><Overline dark>{t.contact.overline}</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">{t.contact.title}</h2></Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-3">
              <a href={`mailto:${t.contact.email}`} data-testid="contact-email-link" className="inline-flex items-center gap-3 text-icdh-white/90 hover:text-icdh-red transition-colors">
                <Mail size={18} className="text-icdh-red" />{t.contact.email}
              </a>
              {t.contact.email2 && (
                <a href={`mailto:${t.contact.email2}`} data-testid="contact-email2-link" className="flex items-center gap-3 text-icdh-white/90 hover:text-icdh-red transition-colors">
                  <Mail size={18} className="text-icdh-red" />{t.contact.email2}
                </a>
              )}
            </div>
          </Reveal>

          <div className="mt-10 grid gap-px bg-icdh-white/10 border border-icdh-white/10" data-testid="contact-locations">
            {t.contact.locations.map((loc, i) => (
              <Reveal key={i} delay={0.2 + i * 0.08}>
                <div className="bg-icdh-navy p-6 md:p-7" data-testid={`contact-loc-${i}`}>
                  <p className="text-xs uppercase tracking-[0.25em] text-icdh-red">{loc.name}</p>
                  <div className="mt-3 flex items-start gap-3 text-icdh-white/85 text-sm">
                    <MapPin size={16} className="text-icdh-red mt-0.5 flex-shrink-0" />
                    <span>{loc.address}</span>
                  </div>
                  <a href={`tel:${loc.phone.replace(/[^+0-9]/g, "")}`} className="mt-2 inline-flex items-center gap-3 text-icdh-white/85 text-sm hover:text-icdh-red transition-colors">
                    <Phone size={16} className="text-icdh-red flex-shrink-0" />
                    <span>{loc.phone}</span>
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.25em] text-icdh-white/60 mb-4">Follow us</p>
              <div className="flex items-center gap-3" data-testid="contact-socials">
                {socials.map(({ icon: Icon, href, label }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                    className="h-11 w-11 flex items-center justify-center border border-icdh-white/25 hover:bg-icdh-red hover:border-icdh-red transition-colors duration-300">
                    <Icon size={18} />
                  </a>
                ))}
                <a href="https://www.tiktok.com/@icdhhaiti?_r=1&_t=ZS-98nUplGSSyT" target="_blank" rel="noreferrer" aria-label="TikTok"
                  className="h-11 w-11 flex items-center justify-center border border-icdh-white/25 hover:bg-icdh-red hover:border-icdh-red transition-colors duration-300 text-sm font-bold">
                  Tk
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8 lg:sticky lg:top-28">
            <input required data-testid="contact-name" className={field} placeholder={t.contact.form.name} value={form.name} onChange={set("name")} />
            <input required type="email" data-testid="contact-email" className={field} placeholder={t.contact.form.email} value={form.email} onChange={set("email")} />
            <textarea required rows={5} data-testid="contact-message" className={`${field} h-auto resize-none pt-3`} placeholder={t.contact.form.message} value={form.message} onChange={set("message")} />
            <button type="submit" disabled={sending} data-testid="contact-submit"
              className="h-14 px-10 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-white hover:text-icdh-navy transition-colors duration-300 disabled:opacity-60">
              {sending ? "..." : t.contact.form.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
